import { LibrarySagaActions } from '@store/librarySaga'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { type ArtBoardMode, selectArtboardMode } from '@store/reducers/artboard'
import { InternalCommentReadsActions } from '@store/reducers/commentReads/actions'
import { managerActions } from '@store/reducers/manager'
import { activityHistoryActions } from '@store/reducers/manager/activityHistory/actions'
import { getActivityHistorySelectedActionId } from '@store/reducers/manager/activityHistory/selectors'
import type { ActivityHistoryAction } from '@store/reducers/manager/activityHistory/types'
import { PROJECT_HISTORY_INIT } from '@store/reducers/manager/middleware/history/types'
import { SocketActions } from '@store/socketSaga'
import { store } from '@store/store'
import type { Action } from 'redux'
import { push } from 'redux-first-history'
import { select } from 'redux-saga/effects'

import { socket } from '../socket'
import { joinUserRoom } from './user'
import { joinWorkspace } from './workspace'

export function* subscribe({
  payload: { id: projectId, authenticated },
}: ReturnType<typeof SocketActions.subscribe>) {
  const selectedPreviewId: number | null = yield select(
    getActivityHistorySelectedActionId
  )

  const mode: ArtBoardMode = yield select(selectArtboardMode)

  const anonymousComments: boolean = yield select(
    migrationProjectSelector.getAnonymousComments
  )

  const canComment: boolean = yield select(
    migrationProjectSelector.getProjectAccessCanComment
  )

  const canReceiveComments = (canComment && authenticated) || anonymousComments

  const onSync = (payload: Action[] | Action) => {
    if (Array.isArray(payload)) {
      const actions = payload

      if (selectedPreviewId) {
        return
      }

      if (mode === 'activity_history') {
        const historyActions: ActivityHistoryAction[] = actions.map(
          (action: any) => ({
            id: action.actionId,
            authorUuid: action.userId,
            type: action.type,
            createdAt: action.createdAt,
          })
        )

        store.dispatch(
          activityHistoryActions.addActivityHistoryActions({
            actions: historyActions,
          })
        )
      }

      // @todo review
      // each time we receive new actions from other users we reset history to prevent undo/redo changes of other user
      actions.push({ type: PROJECT_HISTORY_INIT })

      actions.forEach(store.dispatch)
      return
    }

    store.dispatch(payload)
  }

  socket.on(`sync:${projectId}`, onSync)

  socket.on(`comments:${projectId}`, (payload: Action) => {
    if (!canReceiveComments) {
      return
    }

    store.dispatch(payload)
  })

  // Read marks made in another tab or on another device of the same user.
  // The room is personal, still only the one action shape is let through.
  if (authenticated) {
    joinUserRoom()

    socket.on(`comment-read:${projectId}`, (action: Action) => {
      if (action?.type === InternalCommentReadsActions.update.type) {
        store.dispatch(action)
      }
    })
  }

  socket.on(`delete:${projectId}`, () => {
    store.dispatch(push('/'))
    store.dispatch(managerActions.resetProject())
  })

  socket.on(`refresh:${projectId}`, () => {
    store.dispatch(SocketActions.updateProject())
  })

  // The workspace library: the room carries "re-read these items" only, the
  // data is fetched under the token of the viewer.
  const workspaceId: string | null = yield select(
    migrationProjectSelector.getProjectWorkspaceId
  )

  if (workspaceId) {
    joinWorkspace(workspaceId)
  }

  socket.on('library', (payload) => {
    store.dispatch(LibrarySagaActions.invalidate(payload))
  })
}
